import ShopifyService from './ajax-service';
import Storefront from '../utils/storefront';

export const CART_EVENTS = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  LOADING: 'LOADING',
  FINISHED: 'FINISHED',
};

class CartService {
  constructor() {
    this.cart = {
      id: null,
      subtotal: {
        amount: 0,
        currencyCode: window.TMW?.localization?.currencyCode,
      },
      lines: [],
    };
    this.loading = false;
    this.observers = [];

    this.get();

    // For backwards compatibility we will listen for events
    // However, we should avoid using events and import/call methods on CartService directly
    document.addEventListener('cart:add', (e) => this.add(e.detail?.payload));
    document.addEventListener('cart:updateAttributes', (e) =>
      this.updateAttributes(e.detail?.payload),
    );
    this.mode = ShopifyService.name;
  }

  async callShopify(method, event, ...params) {
    this.loading = true;
    this.notify(CART_EVENTS.LOADING);
    const cart = await ShopifyService[method](this.cart, ...params);
    if (cart) this.cart = cart;
    if (event) this.notify(event);
    this.loading = false;
    this.notify(CART_EVENTS.FINISHED);
    return cart;
  }

  async get(silent = false) {
    return this.callShopify('get', silent ? null : CART_EVENTS.CREATE);
  }

  // eslint-disable-next-line consistent-return
  async add(lines, silent = false) {
    const storefront = new Storefront();
    const quantityLimit = storefront.data?.quantityLimit;
    const { enabled, maxProductQuantity } = quantityLimit;
    const { maxCartQuantity } = quantityLimit;
    let quantityUpdated = false;

    let newLines = !Array.isArray(lines) ? [lines] : lines;

    if (enabled) {
      const cartTotalQuantity = this.cart.items
        .map((item) => item.quantity)
        .reduce((acc, current) => acc + current, 0);
      const cartLineItems = this.cart.items.filter((item) => {
        if (Array.isArray(lines)) {
          return lines.findIndex((line) => line.id.includes(item.id)) > -1;
        }

        return String(lines.id).includes(item.id);
      });

      newLines = newLines.map((newLine) => {
        const { quantity } = newLine;
        const _line = newLine;

        if (cartLineItems.length > 0) {
          let lineQuantity = 0;
          cartLineItems.forEach((lineItem) => {
            lineQuantity += lineItem.quantity;
          });
          const cartQuantity = lineQuantity + quantity;
          if (cartQuantity > maxProductQuantity) {
            _line.quantity = maxProductQuantity - lineQuantity;
            quantityUpdated = true;
          }
        } else if (quantity > maxProductQuantity) {
          _line.quantity = maxProductQuantity;
          quantityUpdated = true;
        }

        return _line;
      });

      if (cartTotalQuantity + newLines[0].quantity > maxCartQuantity) {
        newLines[0].quantity = maxCartQuantity - cartTotalQuantity;
        quantityUpdated = true;

        newLines = newLines.map((newLine, index) => {
          const _newLine = newLine;
          if (index > 0) {
            _newLine.quantity = 0;
          }
          return _newLine;
        });
      }
    }

    const updatedLines = newLines.filter((line) => line.quantity > 0);

    if (quantityUpdated) {
      const event = new CustomEvent('cart:open-error-modal', {
        detail: { message: 'Open cart error modal' },
      });
      window.dispatchEvent(event);
    }

    return this.callShopify(
      'add',
      silent ? null : CART_EVENTS.UPDATE,
      updatedLines,
    );
  }

  async remove(lines, silent = false) {
    return this.callShopify(
      'remove',
      silent ? null : CART_EVENTS.UPDATE,
      lines,
    );
  }

  async update(lines, silent = false) {
    return this.callShopify(
      'update',
      silent ? null : CART_EVENTS.UPDATE,
      lines,
    );
  }

  async updateAttributes(attributes, silent = true) {
    return this.callShopify(
      'updateAttributes',
      silent ? null : CART_EVENTS.UPDATE,
      attributes,
    );
  }

  notify(event) {
    this.observers.forEach((o) => o.call(o, event));
  }

  addListener(observer) {
    this.observers.push(observer);
  }

  removeListener(observer) {
    this.observers = this.observers.filter((o) => o !== observer);
  }
}

const CartServiceSingleton = new CartService();
export default CartServiceSingleton;
